"use client";

import React from "react";
import Head from "next/head";
import Image from "next/image";

const About: React.FC = () => {
  return (
    <>
      <Head>
        <title>About Murray Molohon | Alberta Counselling Services</title>
        <meta
          name="description"
          content="Learn about Murray Molohon, a Registered Psychologist with over 35 years of experience in counseling and mental health at Alberta Counselling Services."
        />
        <meta
          name="keywords"
          content="Murray Molohon, Registered Psychologist, Counseling, Therapy Services, Mental Health Calgary, Alberta Counselling Services"
        />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Head>

      <main className="min-h-screen bg-slate-50">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-r from-emerald-50 to-emerald-100/30 py-24 px-6 lg:py-32">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
              {/* Profile Image */}
              <div className="w-full max-w-xs lg:max-w-sm order-last lg:order-first">
                <div className="relative aspect-square rounded-full border-4 border-emerald-600 shadow-2xl overflow-hidden transform transition-all hover:scale-105 hover:shadow-3xl">
                  <Image
                    src="/murray-molohon-registered-psychologist.webp"
                    alt="Murray Molohon - Registered Psychologist"
                    fill
                    className="object-cover object-top"
                    sizes="(max-width: 1024px) 300px, 400px"
                    priority
                  />
                </div>
              </div>

              {/* Headline */}
              <div className="flex-1 text-center lg:text-left">
                <h1 className="text-4xl md:text-5xl font-bold text-slate-900 leading-tight mb-6">
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-emerald-600 to-teal-700">
                    Murray Molohon
                  </span>
                  <span className="block mt-4 text-3xl md:text-4xl text-slate-800 font-semibold">
                    35+ Years of Transformative Counseling Experience
                  </span>
                </h1>
                <p className="text-lg md:text-xl text-slate-700 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
                  Registered Psychologist dedicated to empowering individuals and couples through
                  evidence-based therapy approaches tailored to your unique needs.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-16 space-y-20">
          {/* Professional Journey */}
          <section className="space-y-12">
            <div className="mb-12 text-center">
              <h2 className="inline-block text-3xl font-bold bg-emerald-600 text-white px-8 py-3 rounded-2xl shadow-lg">
                Professional Journey
              </h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { 
                  year: "1988-1994", 
                  title: "Crisis Intervention Specialist", 
                  description: "Pioneered youth and family crisis programs, building foundational expertise in urgent care situations." 
                },
                { 
                  year: "1994-2004", 
                  title: "Clinical Social Worker", 
                  description: "Developed community mental health initiatives while advocating for systemic social change." 
                },
                { 
                  year: "2004-Present", 
                  title: "Licensed Psychologist", 
                  description: "20+ years providing specialized counseling services with measurable client outcomes." 
                },
              ].map((item, index) => (
                <div 
                  key={index} 
                  className="group relative bg-white p-8 rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 border-l-4 border-emerald-600"
                >
                  <div className="absolute top-6 -left-2.5 w-4 h-4 rounded-full bg-emerald-600" />
                  <div className="text-emerald-600 font-bold text-lg mb-2">{item.year}</div>
                  <h3 className="text-2xl font-semibold text-slate-900 mb-4">{item.title}</h3>
                  <p className="text-slate-700 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Educational Background */}
          <section className="space-y-12">
            <div className="mb-12 text-center">
              <h2 className="inline-block text-3xl font-bold bg-emerald-600 text-white px-8 py-3 rounded-2xl shadow-lg">
                Credentials & Qualifications
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="bg-white p-8 rounded-2xl shadow-md border-t-4 border-emerald-600">
                <h3 className="text-2xl font-semibold text-slate-900 mb-6 flex items-center">
                  <svg 
                    className="w-8 h-8 text-emerald-600 mr-3" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                    <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
                  </svg>
                  Academic Excellence
                </h3>
                <ul className="space-y-4 text-slate-700">
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-emerald-600 rounded-full mt-2 mr-3" />
                    M.Ed. Counseling Psychology - University of Calgary
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-emerald-600 rounded-full mt-2 mr-3" />
                    B.S.W. Social Work - University of Gonzaga
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-emerald-600 rounded-full mt-2 mr-3" />
                    B.R.E. Religious Education - Providence University College
                  </li>
                </ul>
              </div>

              <div className="bg-white p-8 rounded-2xl shadow-md border-t-4 border-emerald-600">
                <h3 className="text-2xl font-semibold text-slate-900 mb-6 flex items-center">
                  <svg
                    className="w-8 h-8 text-emerald-600 mr-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Professional Licensure
                </h3>
                <ul className="space-y-4 text-slate-700">
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-emerald-600 rounded-full mt-2 mr-3" />
                    Registered Psychologist - College of Alberta Psychologists
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-emerald-600 rounded-full mt-2 mr-3" />
                    Member, Psychologists&apos; Association of Alberta
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-emerald-600 rounded-full mt-2 mr-3" />
                    Former CASW-Certified Social Worker
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Philosophy Section */}
          <section className="bg-gradient-to-r from-emerald-600 to-teal-700 rounded-2xl shadow-2xl text-white p-12 transform hover:scale-[101%] transition-all duration-300">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-8">Counseling Philosophy</h2>
              <p className="text-lg leading-relaxed">
              &quot;My approach combines empirically-validated techniques with genuine human connection.
                I believe in creating a safe, non-judgmental space where clients can explore their
                challenges while developing practical strategies for meaningful change. Therapy
                should be both transformative and sustainable – equipping people with tools they
                can use throughout their life journey.&quot;
              </p>
              <div className="mt-8 font-semibold">– Murray Molohon, M.Ed., R.Psych</div>
            </div>
          </section>
        </div>
      </main>
    </>
  );
};

export default About;
